import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../services/api";
import { GiftCard } from "./GiftCardCatalog";

export interface RedemptionModalProps {
  giftCard: GiftCard | null;
  onClose: () => void;
}

// Brand display configuration
const BRAND_LABELS: Record<string, string> = {
  apple: "Apple",
  google: "Google Play",
  amazon: "Amazon",
};

interface RedeemResponse {
  success: boolean;
  data: {
    redemption: {
      id: string;
      status: string;
    };
  };
}

async function redeemGiftCard(giftCardId: string): Promise<RedeemResponse> {
  const response = await api.post<RedeemResponse>("/gift-cards/redeem", {
    gift_card_id: giftCardId,
  });
  return response.data;
}

export function RedemptionModal({ giftCard, onClose }: RedemptionModalProps) {
  const queryClient = useQueryClient();

  const redeemMutation = useMutation({
    mutationFn: () => {
      if (!giftCard) throw new Error("No gift card selected");
      return redeemGiftCard(giftCard.id);
    },
    onSuccess: () => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["gift-cards"] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      queryClient.invalidateQueries({ queryKey: ["points"] });
      queryClient.invalidateQueries({ queryKey: ["user-points"] });
      queryClient.invalidateQueries({ queryKey: ["redemptions"] });
    },
  });

  // Don't render if no gift card is selected
  if (!giftCard) return null;

  const brandLabel = BRAND_LABELS[giftCard.brand] || giftCard.brand;
  const isSuccess = redeemMutation.isSuccess;
  const isError = redeemMutation.isError;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={!redeemMutation.isPending ? onClose : undefined}
      />

      {/* Modal */}
      <div className="relative bg-dark-800 border border-dark-border rounded-xl shadow-2xl max-w-md w-full p-6 z-10">
        {/* Close button */}
        {!redeemMutation.isPending && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-200 transition-colors"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Header */}
        <div className="text-center mb-6">
          {isSuccess ? (
            <>
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-100">Redemption Successful!</h2>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-100">Confirm Redemption</h2>
            </>
          )}
        </div>

        {/* Content */}
        {isSuccess ? (
          <div className="text-center space-y-4">
            <p className="text-gray-300">
              Your <span className="font-semibold text-white">{brandLabel} ${giftCard.amount}</span> gift card will be delivered to your email within 24 hours.
            </p>
            <p className="text-sm text-gray-400">
              You can track your redemption status in your profile.
            </p>
            <button
              onClick={onClose}
              className="btn btn-primary w-full mt-4"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            {/* Gift Card Details */}
            <div className="bg-dark-700 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">{brandLabel}</p>
                  <p className="text-xl font-bold text-gray-100">${giftCard.amount} Gift Card</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-400">Points to be deducted</p>
                  <p className="text-xl font-bold text-primary-300">{giftCard.point_cost.toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Error message */}
            {isError && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-3 text-red-400">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm">
                    {redeemMutation.error?.response?.data?.error ||
                     redeemMutation.error?.message ||
                     "Failed to redeem gift card. Please try again."}
                  </p>
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="btn flex-1 bg-dark-600 hover:bg-dark-500 text-gray-300"
                disabled={redeemMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={() => redeemMutation.mutate()}
                className="btn btn-primary flex-1"
                disabled={redeemMutation.isPending}
              >
                {redeemMutation.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Redeeming...
                  </span>
                ) : (
                  "Redeem"
                )}
              </button>
            </div>

            {/* Footer info */}
            <p className="text-xs text-gray-500 text-center mt-4">
              Gift cards are delivered electronically within 24 hours.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default RedemptionModal;
